/**
 * Client-side company logo lookup via Logo.dev — ported from the former
 * Glide app (apps/viewpoint-ops/client/lib/logo.ts in the VP Service Glide
 * project), same token, same domain-guessing rules, so the same clients
 * resolve to the same logos. Neither app stores an actual logo image;
 * both derive one at render time from the company's website domain (or a
 * best-guess .com from its name), with a caller-side fallback to initials
 * when the image 404s (unknown domain, no logo on file at Logo.dev, etc).
 */
const TOKEN = "pk_J_YRBxAZTiCYmvmBUYlWzw";

export function logoUrl(
  website: string | null | undefined,
  name: string | null | undefined,
): string {
  const domain = extractDomain(website) ?? guessDomain(name);
  if (!domain) return "";
  // fallback=404 turns off Logo.dev's default behaviour of serving a
  // generic placeholder glyph (HTTP 200, a couple hundred bytes) for any
  // domain it can't resolve -- confirmed live: without this, a synthetic
  // dev company on a fake domain like example.invalid, or any real company
  // with no logo on file, silently renders that placeholder instead of
  // ever reaching CompanyLogo's own onError fallback to initials, since
  // the request never actually fails.
  return `https://img.logo.dev/${domain}?token=${TOKEN}&size=64&format=png&fallback=404`;
}

function extractDomain(website: string | null | undefined): string | null {
  if (!website) return null;
  const raw = website
    .trim()
    .replace(/^(https?:\/\/)?(www\.)?/, "")
    .split("/")[0];
  return raw || null;
}

function guessDomain(name: string | null | undefined): string | null {
  if (!name) return null;
  const cleaned = name
    .toLowerCase()
    .replace(
      /\s+(ltd|llp|llc|plc|inc|group|limited|association|council|homes|community|gateway|brands|flight|eye|hub|burtons companies.*)\b.*/g,
      "",
    )
    .replace(/[^a-z0-9]/g, "")
    .trim();
  return cleaned ? `${cleaned}.com` : null;
}
